import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Ribbon } from "@/components/Layout";
import { toast } from "sonner";
import { Plus, MapPin, Lock, BarChart3 } from "lucide-react";
import { STATUT_LABEL, STATUT_CLR, frRange } from "@/lib/tournee";

// Liste des tournées (maquette 2h évoluée) : groupes Brouillons / Actives / Terminées / Annulées.
const GROUPES = [["brouillon", "Brouillons"], ["active", "Actives"], ["terminee", "Terminées"], ["annulee", "Annulées"]];

export default function Tournees() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [secteurs, setSecteurs] = useState([]);

  const load = () => api.get("/tournees").then((r) => setItems(r.data));
  useEffect(() => { load(); api.get("/settings/secteurs").then((r) => setSecteurs(r.data)); }, []);

  const creer = async () => {
    try { const { data } = await api.post("/tournees", { titre: "Nouvelle tournée" }); nav(`/tournees/${data.id}`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const grouped = (st) => items.filter((t) => (t.statut_norm || t.statut) === st);

  return (
    <div>
      <Ribbon groups={[{ name: "Actions", children: (
        <button data-testid="new-tournee-btn" onClick={creer} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#141821] text-white text-sm font-medium"><Plus className="w-4 h-4" />Tournée</button>) }]} />

      {items.length === 0 && <div className="text-sm text-slate-400 bg-white rounded-xl border border-slate-200 p-6 text-center">Aucune tournée. Créez votre première tournée de soin.</div>}

      <div className="space-y-6">
        {GROUPES.map(([st, titre]) => { const list = grouped(st); if (!list.length) return null; return (
          <div key={st} data-testid={`tournee-group-${st}`}>
            <div className="flex items-center gap-2 mb-2"><h3 className="text-[13px] font-semibold" style={{ color: "var(--ink)" }}>{titre}</h3><span className="mono text-[12px]" style={{ color: "var(--muted-2)" }}>{list.length}</span></div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {list.map((t) => <Carte key={t.id} t={t} secteurs={secteurs} onOpen={(bilan) => nav(`/tournees/${t.id}${bilan ? "?tab=bilan" : ""}`)} />)}
            </div>
          </div>
        ); })}
      </div>
    </div>
  );
}

function Carte({ t, secteurs, onOpen }) {
  const st = t.statut_norm || t.statut;
  const clr = STATUT_CLR[st] || STATUT_CLR.brouillon;
  const nbSel = (t.patient_statuts || []).filter((s) => s.selectionne !== false).length || (t.patients?.length || 0);
  const complete = false; // indicateur calculé (capacités) — Phase 3+
  const secteur = secteurs.find((s) => s.id === t.secteur_id)?.nom;
  const dateTxt = (t.periode_debut || t.periode_fin) ? frRange(t.periode_debut, t.periode_fin) : null;
  return (
    <div data-testid={`tournee-card-${t.id}`} onClick={() => onOpen(false)} className="bg-white sx-card p-4 cursor-pointer hover:shadow-md transition-all">
      <div className="flex items-start justify-between gap-2">
        <div className="font-display font-semibold" style={{ color: "var(--ink)" }}><span style={{ color: "#8a8f9c" }}>Tournée </span>{t.titre || "sans titre"}</div>
        <span className="text-[11px] px-2 py-0.5 rounded-full shrink-0" style={{ background: clr.bg, color: clr.fg }}>{STATUT_LABEL[st] || st}</span>
      </div>
      <div className="text-[12.5px] mt-1" style={{ color: "var(--text-2)" }}>{[dateTxt, `${nbSel} patient${nbSel > 1 ? "s" : ""}`, secteur].filter(Boolean).join(" · ")}</div>
      <div className="flex items-center gap-1.5 mt-2 flex-wrap">
        {complete && <span className="text-[11px] px-2 py-0.5 rounded-full" style={{ background: "oklch(0.94 0.05 155)", color: "oklch(0.4 0.1 155)" }}>Complète</span>}
        {t.figee && <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full" style={{ background: "#f1f1ed", color: "#4a5060" }}><Lock className="w-3 h-3" />Figée</span>}
        {st === "terminee" && <button data-testid={`tournee-bilan-${t.id}`} onClick={(e) => { e.stopPropagation(); onOpen(true); }} className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full ml-auto" style={{ border: "1px solid var(--border)", color: "var(--text-2)" }}><BarChart3 className="w-3 h-3" />Bilan</button>}
      </div>
    </div>
  );
}
